/**
 * Shared scroll-motion primitives for the About and How It Works pages.
 *
 * Built only on the already-installed `framer-motion` (no new dependencies).
 * Everything here animates transform / opacity (plus tiny, one-shot blur on
 * small elements) and relies on motion values, so scrolling and pointer
 * movement never trigger React re-renders.
 *
 * Motion levels (see useMotionLevel):
 *   'none' – prefers-reduced-motion, or no IntersectionObserver.
 *            Everything renders in its final state, in document order.
 *   'lite' – tablet / touch. Short staggered reveals only. No tilt, no
 *            cursor tracking, no parallax, no pinning.
 *   'full' – desktop with a fine pointer. Parallax, tilt, cursor depth.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1];
const CYAN = '#00f0ff';

/* ── Motion level ───────────────────────────────────────────────────────────── */

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange); // Safari < 14
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

export function useMotionLevel() {
  const reduced = useReducedMotion();
  const tablet = useMediaQuery('(max-width: 1023px)');
  const touch = useMediaQuery('(hover: none), (pointer: coarse)');
  // Without IntersectionObserver whileInView would never fire and content
  // would stay hidden, so fall back to the static rendering.
  if (reduced || typeof IntersectionObserver === 'undefined') return 'none';
  return tablet || touch ? 'lite' : 'full';
}

/* ── Scroll progress (slim cyan bar under the navbar) ───────────────────────── */

export function ScrollProgress() {
  const level = useMotionLevel();
  const { scrollYProgress } = useScroll();
  const smooth = useSpring(scrollYProgress, { stiffness: 140, damping: 28, mass: 0.4 });
  if (typeof document === 'undefined') return null;
  // Portalled to <body> so the route-transition transform in App.jsx can never
  // offset a position:fixed element. Sits directly under the 64px navbar.
  return createPortal(
    <motion.div
      aria-hidden="true"
      style={{
        position: 'fixed', top: 64, left: 0, right: 0, height: 2, zIndex: 99,
        pointerEvents: 'none', transformOrigin: '0% 50%',
        scaleX: level === 'none' ? scrollYProgress : smooth,
        background: `linear-gradient(90deg, rgba(0,240,255,0.35), ${CYAN})`,
        boxShadow: '0 0 10px rgba(0,240,255,0.55)',
      }}
    />,
    document.body,
  );
}

/* ── Reveal: fade + rise + blur reduction ───────────────────────────────────── */

export function Reveal({
  children, delay = 0, x = 0, y = 28, scale = 1, blur = 4, style, className,
}) {
  const level = useMotionLevel();
  const ref = useRef(null);
  if (level === 'none') return <div className={className} style={style}>{children}</div>;

  const k = level === 'lite' ? 0.6 : 1;
  const from = { opacity: 0, x: x * k, y: y * k, scale: 1 - (1 - scale) * k };
  const to = { opacity: 1, x: 0, y: 0, scale: 1 };
  if (blur > 0) {
    from.filter = `blur(${blur * k}px)`;
    to.filter = 'blur(0px)';
  }
  return (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial={from}
      whileInView={to}
      viewport={{ once: true, amount: 0.15, margin: '0px 0px -6% 0px' }}
      transition={{ duration: 0.75, delay, ease: EASE }}
      // A lingering filter would create a stacking context and neutralise the
      // backdrop-filter on .glass children, so clear it once the reveal ends.
      onAnimationComplete={() => { if (ref.current) ref.current.style.filter = 'none'; }}
    >
      {children}
    </motion.div>
  );
}

/* ── WordReveal: per-word heading reveal (real text, selectable, readable) ──── */

export function WordReveal({
  text, inView = false, delay = 0.2, stagger = 0.055, className, style,
}) {
  const level = useMotionLevel();
  if (level === 'none') return <span className={className} style={style}>{text}</span>;

  const dist = level === 'lite' ? '0.28em' : '0.45em';
  const trigger = inView
    ? { whileInView: { opacity: 1, y: '0em' }, viewport: { once: true, amount: 0.6 } }
    : { animate: { opacity: 1, y: '0em' } };
  let w = -1;
  return (
    <span className={className} style={style}>
      {text.split(/(\s+)/).map((tok, i) => {
        if (tok === '') return null;
        if (/^\s+$/.test(tok)) return tok; // keep real spaces so wrapping/selection stay natural
        w += 1;
        return (
          <motion.span
            key={i}
            style={{ display: 'inline-block' }}
            initial={{ opacity: 0, y: dist }}
            {...trigger}
            transition={{ duration: 0.6, delay: delay + w * stagger, ease: EASE }}
          >
            {tok}
          </motion.span>
        );
      })}
    </span>
  );
}

/* ── TiltCard: damped 3D tilt + cyan border illumination ────────────────────── */

const TILT_SPRING = { stiffness: 180, damping: 18, mass: 0.6 };

export function TiltCard({ children, color = CYAN, max = 5, fill = false, style }) {
  const level = useMotionLevel();
  const full = level === 'full';
  const ref = useRef(null);
  const [lit, setLit] = useState(false);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const sx = useSpring(rx, TILT_SPRING);
  const sy = useSpring(ry, TILT_SPRING);

  const onMove = (e) => {
    if (!full || e.pointerType !== 'mouse' || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * max * 2);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * max * 2);
  };
  const leave = () => { rx.set(0); ry.set(0); setLit(false); };

  // `initial/animate/whileHover` labels let <HoverIcon> children react to hover.
  const hoverProps = full ? { initial: 'rest', animate: 'rest', whileHover: 'hover' } : {};
  const tiltStyle = full ? { rotateX: sx, rotateY: sy, transformPerspective: 900 } : {};

  return (
    <motion.div
      ref={ref}
      {...hoverProps}
      onPointerMove={onMove}
      onPointerEnter={() => setLit(true)}
      onPointerLeave={leave}
      onFocusCapture={() => setLit(true)}
      onBlurCapture={() => setLit(false)}
      style={{ position: 'relative', height: fill ? '100%' : undefined, ...tiltStyle, ...style }}
    >
      {children}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, borderRadius: 8, pointerEvents: 'none',
          border: `1px solid ${color}80`,
          boxShadow: `0 0 22px ${color}22, inset 0 0 18px ${color}10`,
          opacity: lit ? 1 : 0, transition: 'opacity 0.25s',
        }}
      />
    </motion.div>
  );
}

/* Icon tile that rotates / glows when its parent <TiltCard> is hovered. */
export function HoverIcon({ color = CYAN, restShadow, style, children }) {
  return (
    <motion.div
      aria-hidden="true"
      variants={{
        rest: { rotate: 0, scale: 1, boxShadow: restShadow || `0 0 0px ${color}00` },
        hover: { rotate: 8, scale: 1.1, boxShadow: `0 0 18px ${color}55` },
      }}
      transition={{ type: 'spring', stiffness: 260, damping: 16 }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/* ── Parallax: element drifts slightly against scroll (desktop only) ────────── */

export function Parallax({ children, distance = 12, style }) {
  const level = useMotionLevel();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const d = level === 'full' ? distance : 0;
  const y = useTransform(scrollYProgress, [0, 1], [d, -d]);
  return <motion.div ref={ref} style={{ height: '100%', ...style, y }}>{children}</motion.div>;
}

/* ── ScanSweep: one-shot cyan band that sweeps across a card as it enters ───── */

export function ScanSweep({ delay = 0.25 }) {
  const level = useMotionLevel();
  if (level === 'none') return null;
  return (
    <motion.div
      aria-hidden="true"
      initial={{ x: '-100%', opacity: 0 }}
      whileInView={{ x: '400%', opacity: [0, 1, 1, 0] }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 1.4, delay, ease: 'easeInOut' }}
      style={{
        position: 'absolute', top: 0, bottom: 0, left: 0, width: '30%', pointerEvents: 'none',
        background: 'linear-gradient(90deg, transparent 0%, rgba(0,240,255,0.07) 70%, rgba(0,240,255,0.3) 100%)',
      }}
    />
  );
}

/* ── ChapterLine: thin connector that draws itself between sections ─────────────
 * Zero-height wrapper; the line lives inside the existing 3.5rem section gap, so
 * it never changes layout. */

export function ChapterLine({ style }) {
  const level = useMotionLevel();
  const animated = level !== 'none';
  return (
    <div aria-hidden="true" style={{ position: 'relative', height: 0, ...style }}>
      <motion.div
        initial={animated ? { scaleY: 0, opacity: 0 } : false}
        whileInView={animated ? { scaleY: 1, opacity: 1 } : undefined}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ duration: 0.8, ease: EASE }}
        style={{
          position: 'absolute', left: '50%', marginLeft: -0.5, top: '-3rem',
          height: '2.5rem', width: 1, originY: 0,
          background: 'linear-gradient(180deg, rgba(0,240,255,0), rgba(0,240,255,0.55))',
        }}
      />
    </div>
  );
}

/* ── HeroBackdrop: three-layer parallax decoration behind a page hero ──────────
 * Sits INSIDE the page (absolute, pointer-events none, aria-hidden). It never
 * touches the global MatrixBackground. Back layer moves slowest, mid moderately,
 * foreground labels fastest; desktop adds damped cursor depth. */

const SVG_FILL = { position: 'absolute', inset: 0, width: '100%', height: '100%' };
const MASK = 'radial-gradient(ellipse 60% 75% at 50% 50%, #000 35%, transparent 100%)';
const PIPE_X = [80, 200, 320, 440, 560, 680, 800, 920];
const FX_CSS = '.arise-fx-chip{display:none}@media (min-width:1024px){.arise-fx-chip{display:block}}';

function RingsBack() {
  return (
    <svg viewBox="0 0 1000 340" preserveAspectRatio="xMidYMid meet" style={SVG_FILL}>
      <g fill="none" stroke={CYAN} strokeWidth="1">
        <circle cx="500" cy="170" r="70" opacity="0.16" />
        <circle cx="500" cy="170" r="120" opacity="0.11" />
        <circle cx="500" cy="170" r="170" opacity="0.08" />
        <circle cx="500" cy="170" r="145" opacity="0.12" strokeDasharray="2 9" />
        <line x1="120" y1="170" x2="880" y2="170" opacity="0.07" />
        <line x1="500" y1="0" x2="500" y2="340" opacity="0.07" />
        <polygon points="500,136 529.4,153 529.4,187 500,204 470.6,187 470.6,153" opacity="0.28" />
      </g>
      <circle cx="500" cy="170" r="3" fill={CYAN} opacity="0.6" />
    </svg>
  );
}

function RingsMid({ spin }) {
  return (
    <svg viewBox="0 0 1000 340" preserveAspectRatio="xMidYMid meet" style={SVG_FILL}>
      <motion.circle
        cx="500" cy="170" r="120" fill="none" stroke={CYAN} strokeWidth="1.5"
        strokeLinecap="round" strokeDasharray="110 650" opacity="0.5"
        animate={spin ? { rotate: 360 } : undefined}
        transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
      />
      <motion.circle
        cx="500" cy="170" r="170" fill="none" stroke={CYAN} strokeWidth="1"
        strokeLinecap="round" strokeDasharray="60 1000" opacity="0.3"
        animate={spin ? { rotate: -360 } : undefined}
        transition={{ duration: 70, repeat: Infinity, ease: 'linear' }}
      />
    </svg>
  );
}

function PipeBack() {
  return (
    <svg viewBox="0 0 1000 340" preserveAspectRatio="xMidYMid meet" style={SVG_FILL}>
      <g fill="none" stroke={CYAN} strokeWidth="1">
        <line x1="80" y1="170" x2="920" y2="170" opacity="0.14" />
        {PIPE_X.map((x) => (
          <g key={x}>
            <circle cx={x} cy="170" r="7" opacity="0.28" />
            <line x1={x} y1="150" x2={x} y2="190" opacity="0.1" />
          </g>
        ))}
      </g>
    </svg>
  );
}

function PipeMid({ spin }) {
  if (!spin) return null;
  return (
    <svg viewBox="0 0 1000 340" preserveAspectRatio="xMidYMid meet" style={SVG_FILL}>
      {[0, 3].map((d) => (
        <g key={d}>
          <motion.circle
            cy="170" r="9" fill={CYAN} initial={{ cx: 80, opacity: 0 }}
            animate={{ cx: [80, 920], opacity: [0, 0.18, 0.18, 0] }}
            transition={{ duration: 6, delay: d, repeat: Infinity, ease: 'linear', times: [0, 0.08, 0.92, 1] }}
          />
          <motion.circle
            cy="170" r="3.5" fill={CYAN} initial={{ cx: 80, opacity: 0 }}
            animate={{ cx: [80, 920], opacity: [0, 1, 1, 0] }}
            transition={{ duration: 6, delay: d, repeat: Infinity, ease: 'linear', times: [0, 0.08, 0.92, 1] }}
          />
        </g>
      ))}
    </svg>
  );
}

export function HeroBackdrop({ variant = 'rings', labels = [] }) {
  const level = useMotionLevel();
  const full = level === 'full';
  const k = full ? 1 : 0;
  const { scrollY } = useScroll();

  // Cursor depth: raw -0.5..0.5 position, heavily damped by a soft spring.
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const cx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.8 });
  const cy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.8 });

  useEffect(() => {
    if (!full) return undefined;
    const onMove = (e) => {
      mx.set(e.clientX / window.innerWidth - 0.5);
      my.set(e.clientY / window.innerHeight - 0.5);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [full, mx, my]);

  const backX = useTransform(cx, (v) => v * -14 * k);
  const midX = useTransform(cx, (v) => v * -28 * k);
  const foreX = useTransform(cx, (v) => v * -48 * k);
  const backY = useTransform([scrollY, cy], ([s, c]) => (Math.min(s, 450) * 0.15 + c * -14) * k);
  const midY = useTransform([scrollY, cy], ([s, c]) => (Math.min(s, 450) * 0.05 + c * -28) * k);
  const foreY = useTransform([scrollY, cy], ([s, c]) => (Math.min(s, 450) * -0.1 + c * -48) * k);
  const fade = useTransform(scrollY, [0, 480], [1, 0]);

  const layer = { position: 'absolute', inset: 0 };
  return (
    <motion.div
      aria-hidden="true"
      style={{
        position: 'absolute', inset: '-1.5rem 0', zIndex: 0, pointerEvents: 'none',
        opacity: level === 'none' ? 1 : fade,
        WebkitMaskImage: MASK, maskImage: MASK,
      }}
    >
      <motion.div style={{ ...layer, x: backX, y: backY }}>
        {variant === 'pipeline' ? <PipeBack /> : <RingsBack />}
      </motion.div>
      <motion.div style={{ ...layer, x: midX, y: midY }}>
        {variant === 'pipeline' ? <PipeMid spin={level !== 'none'} /> : <RingsMid spin={level !== 'none'} />}
      </motion.div>
      {labels.length > 0 && (
        <motion.div style={{ ...layer, x: foreX, y: foreY }}>
          {labels.map((l) => (
            <span
              key={l.text}
              className="arise-fx-chip"
              style={{
                position: 'absolute', fontFamily: 'Space Mono, monospace', fontSize: 9,
                letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(0,240,255,0.55)',
                border: '1px solid rgba(0,240,255,0.18)', background: 'rgba(2,8,16,0.55)',
                borderRadius: 3, padding: '3px 8px', ...l.pos,
              }}
            >
              {l.text}
            </span>
          ))}
        </motion.div>
      )}
      <style>{FX_CSS}</style>
    </motion.div>
  );
}
